/**
 * The control bar, while something is playing on the television.
 *
 * ## Why it exists at all
 *
 * Chromium's own bar is there and it is useless here: it expects a pointer, so a DPAD
 * cannot reach it, and it covers the bottom of the picture with controls nobody can
 * operate. Turning it off left the player with no on-screen state whatsoever — no
 * position, no duration, no indication that a press had registered. This replaces it with
 * something the remote already drives.
 *
 * ## Why it shows nothing until a key is pressed
 *
 * A bar over the picture is an interruption. It appears on the first press, stays while
 * presses keep arriving, and leaves on its own — the same behaviour every television has,
 * for the same reason: the viewer asked a question, it answered, and then it got out of
 * the way.
 *
 * ## Why it polls
 *
 * `timeupdate` fires about four times a second on a media element and not at all for
 * AVPlay, which reports through its own callback. One timer that reads whatever the
 * current source of truth is covers both, and it only runs while the bar is visible — so
 * the cost is zero for the 99% of a film during which nothing is on screen.
 */
import { useEffect, useRef, useState } from 'react';
import type { EngineControls } from '../../player/engines';

/** Long enough to read a timestamp, short enough not to sit over a face. */
const HIDE_AFTER_MS = 4000;
const TICK_MS = 500;

export function clock(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) return '0:00';
  const whole = Math.floor(sec);
  const h = Math.floor(whole / 3600);
  const m = Math.floor((whole % 3600) / 60);
  const s = whole % 60;
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  return `${h > 0 ? `${h}:` : ''}${mm}:${String(s).padStart(2, '0')}`;
}

export interface TvPlayerBarProps {
  title: string;
  isLive: boolean;
  video: HTMLVideoElement | null;
  /**
   * The engine's controls, as a getter.
   *
   * The engine lives in a ref, so it can attach after this component last rendered — a
   * value captured at render time would stay null for the whole film, and on AVPlay that
   * means a bar frozen at 0:00 with no duration.
   */
  controls: () => EngineControls | null;
  /** Provider metadata, used when the stream itself cannot say how long it is. */
  fallbackDurationSec?: number | null;
  /** Bumped by the key handler on every press; each change re-shows the bar. */
  activity: number;
  /** The transient message from the key handler — "⏩ 1:12:30" and friends. */
  hint: string | null;
  /**
   * Where a burst of seek presses has reached, before it is committed to the player.
   *
   * Drawn instead of the playback position while it is set. Presses are collected for half
   * a second so the remux engine is not asked to restart the conversion once per press,
   * and without something visible moving during that half second the remote would appear
   * to have ignored all but the last of them.
   */
  pendingSeek?: number | null;
  /** Whether Up opens the track menu, so the key line can say so only when it is true. */
  canOpenMenu?: boolean;
}

export function TvPlayerBar({
  title,
  isLive,
  video,
  controls,
  fallbackDurationSec,
  activity,
  hint,
  pendingSeek = null,
  canOpenMenu = false,
}: TvPlayerBarProps) {
  const [visible, setVisible] = useState(true);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState<number | null>(null);
  const [paused, setPaused] = useState(false);

  // Held in a ref because the caller passes an inline arrow: as a dependency it would be
  // a new identity every render, tearing down and re-creating the interval each time.
  const controlsRef = useRef(controls);
  controlsRef.current = controls;

  // Every press re-arms the timer rather than toggling: holding a skip key should keep
  // the bar up, not flicker it.
  useEffect(() => {
    setVisible(true);
    const timer = window.setTimeout(() => setVisible(false), HIDE_AFTER_MS);
    return () => window.clearTimeout(timer);
  }, [activity, hint]);

  useEffect(() => {
    if (!visible) return;

    const read = () => {
      const engine = controlsRef.current();
      if (engine) {
        setPosition(engine.currentTime());
        setDuration(engine.durationSec() ?? fallbackDurationSec ?? null);
        setPaused(engine.paused());
        return;
      }
      if (!video) return;
      setPosition(video.currentTime);
      // The element's own duration wins when it has one: the provider's metadata is
      // frequently wrong, and the file being played is the authority on its own length.
      const own = video.duration;
      setDuration(Number.isFinite(own) && own > 0 ? own : (fallbackDurationSec ?? null));
      setPaused(video.paused);
    };

    read();
    const timer = window.setInterval(read, TICK_MS);
    return () => window.clearInterval(timer);
  }, [visible, video, fallbackDurationSec]);

  // Kept visible while a seek is pending even if the hide timer has run out: the bar
  // vanishing mid-scrub would take the position readout away exactly when it is being used.
  if (!visible && pendingSeek === null) return null;

  // The pending position wins while it is set — that is the whole feedback for a burst of
  // presses. Playback carries on underneath, which is why the two are drawn as two marks
  // rather than one: the fill stays where the film is, and the ghost shows where it is
  // going.
  const at = pendingSeek ?? position;
  const pct = (value: number) =>
    duration && duration > 0 ? Math.min(100, Math.max(0, (value / duration) * 100)) : 0;
  const percent = pct(position);

  return (
    <div className="tvbar" aria-hidden="true">
      <div className="tvbar__top">
        <span className="tvbar__state">{paused ? '⏸' : '▶'}</span>
        <span className="tvbar__title">{title}</span>
        {hint && <span className="tvbar__hint">{hint}</span>}
      </div>

      {isLive ? (
        <div className="tvbar__live">CANLI</div>
      ) : (
        <>
          <div className={`tvbar__track${pendingSeek !== null ? ' is-seeking' : ''}`}>
            <span style={{ width: `${percent}%` }} />
            {/* Drawn as a second fill with a bright edge rather than a floating marker:
                the track clips its overflow, so a marker centred on its own position would
                be half missing at either end of the film — which is exactly where a viewer
                holding a skip key ends up. */}
            {pendingSeek !== null && duration ? (
              <i className="tvbar__ghost" style={{ width: `${pct(pendingSeek)}%` }} />
            ) : null}
          </div>
          <div className="tvbar__times">
            <span className={pendingSeek !== null ? 'is-pending' : undefined}>{clock(at)}</span>
            {/* Nothing rather than a fake total: a stream with no known duration is
                common here, and "0:00" in that slot reads as a broken player. */}
            <span>{duration ? clock(duration) : ''}</span>
          </div>
        </>
      )}

      <div className="tvbar__keys">
        ← / → sar · OK duraklat{canOpenMenu ? ' · ▲ ses/altyazı' : ''} · Geri çık
      </div>
    </div>
  );
}
